import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import tenantService from '../../services/tenantService';
import memberService from '../../services/memberService';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import api from '../../services/api';
// Import Material UI components
import {
  Box, Grid, Paper, Typography, TextField, Button,
  Switch, FormControlLabel, FormGroup, Tabs, Tab,
  Divider, Alert, CircularProgress, useTheme,
  List, ListItem, ListItemIcon, ListItemText, Chip, IconButton, 
  Card, CardContent, Checkbox, Accordion, AccordionSummary, AccordionDetails,
  Avatar, Badge, Container
} from '@mui/material';
// Import Material UI icons
import {
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Code as CodeIcon,
  Business as BusinessIcon,
  ViewList as ViewListIcon,
  People as PeopleIcon,
  Payment as PaymentIcon,
  AccountBalance as BankIcon,
  ExpandMore as ExpandMoreIcon,
  PhotoCamera as PhotoCameraIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon
} from '@mui/icons-material';

const ENDPOINT = '/tenants'; // Add this constant for debug info

const TenantSettingsPage = () => {
  const { tenant, updateTenantSettings, refreshTenant } = useTenant();
  const { user } = useAuth();
  const theme = useTheme();
  const { primaryColor, setPrimaryColor } = useThemeContext();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [showDebug, setShowDebug] = useState(false);
  const [isLocalOnly, setIsLocalOnly] = useState(false);
  const [tenantId, setTenantId] = useState(null);
  const [previewColor, setPreviewColor] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const fileInputRef = useRef(null);
  
  // Payment settings state
  const [paymentSettings, setPaymentSettings] = useState({
    bankName: '',
    bankAccountNumber: '',
    bankRoutingNumber: '',
    zelleEmail: '',
    zellePhone: '',
    paypalEmail: '',
    paypalLink: '',
    acceptedPaymentMethods: ['stripe']
  });
  
  const [settings, setSettings] = useState({
    directory: {
      enabled: false,
      visibleFields: ['firstName', 'lastName', 'membershipType', 'joinDate']
    }
  });
  
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    mode: 'onBlur'
  });

  // Password change form
  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors },
    reset: resetPassword,
    watch: watchPassword
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }
  });

  const newPassword = watchPassword('newPassword', '');

  // Handle password change
  const onPasswordSubmit = async (data) => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccess(null);
      
      // Check authentication before submitting
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please login again.');
        return;
      }
      
      // Validate passwords match
      if (data.newPassword !== data.confirmPassword) {
        setError('New passwords do not match');
        return;
      }
      
      // Use memberService to change password
      await memberService.changePassword(data.currentPassword, data.newPassword);
      
      // Show success message
      setSuccess('Password changed successfully');
      resetPassword();
      
      // Send notification email about password change
      try {
        const emailData = {
          to: user?.email,
          subject: 'Your Password Has Been Changed',
          template: 'password-changed',
          data: {
            name: user?.firstName || 'Admin',
            timestamp: new Date().toLocaleString(),
            supportEmail: 'support@membasuite.com'
          }
        };
        
        // Optional: Send notification email through API
        await api.post('/notifications/email', emailData);
        console.log('Password change notification email sent');
      } catch (emailError) {
        console.warn('Failed to send password change notification email:', emailError);
        // Continue with success flow even if email notification failed
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (error) {
      console.error('Error changing password:', error);
      
      // Add more detailed error handling for auth errors
      if (error.response?.status === 401) {
        setError('Your session has expired. Please login again to continue.');
      } else if (error.response?.data?.message?.includes('does not have a password set')) {
        setError('Your account does not have a password set. Please use the Forgot Password feature to set a new password.');
      } else {
        setError(error.response?.data?.message || 'Failed to change password');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Watch for changes to primaryColor field to enable live preview
  const watchedPrimaryColor = watch('primaryColor');
  
  // Update preview color when form color changes
  useEffect(() => {
    if (watchedPrimaryColor && watchedPrimaryColor !== previewColor) {
      setPreviewColor(watchedPrimaryColor);
      // Apply color immediately for preview
      setPrimaryColor(watchedPrimaryColor);
    }
  }, [watchedPrimaryColor, previewColor, setPrimaryColor]);

  // Get tenant ID from different sources
  useEffect(() => {
    // First try to get from tenant object
    if (tenant && (tenant._id || tenant.id)) {
      setTenantId(tenant._id || tenant.id);
      return;
    }
    
    // If not available, try localStorage
    const storedTenantId = localStorage.getItem('tenantId');
    if (storedTenantId) {
      setTenantId(storedTenantId);
      return;
    }
    
    // As last resort, try to get from currentTenant in localStorage
    try {
      const currentTenantStr = localStorage.getItem('currentTenant');
      if (currentTenantStr) {
        const currentTenant = JSON.parse(currentTenantStr);
        if (currentTenant && (currentTenant._id || currentTenant.id)) {
          setTenantId(currentTenant._id || currentTenant.id);
          return;
        }
      }
    } catch (e) {
      console.error('Error parsing currentTenant from localStorage:', e);
    }
    
    // If no tenant ID found anywhere, show an error
    if (!tenantId) {
      setError('Cannot identify tenant. Please try logging out and back in.');
      setShowDebug(true);
    }
  }, [tenant]);

  // Load initial data from both local storage and server
  const loadInitialData = async () => {
    try {
      // First, load whatever we have in tenant from context
      if (tenant) {
        reset({
          name: tenant.name,
          logo: tenant.logo,
          primaryColor: tenant.primaryColor || '#3f51b5', // Provide default if missing
          allowSelfRegistration: tenant.settings?.allowSelfRegistration
        });

        // Set the preview color initially
        if (tenant.primaryColor) {
          setPreviewColor(tenant.primaryColor);
        }

        // Set the logo preview if available
        if (tenant.logo) {
          setLogoPreview(tenant.logo);
        }

        // Initialize directory settings
        setSettings({
          directory: tenant.settings?.directory || {
            enabled: false,
            visibleFields: ['firstName', 'lastName', 'membershipType', 'joinDate']
          }
        });
        
        // Initialize payment settings from tenant data if available
        if (tenant.paymentSettings) {
          setPaymentSettings({
            // Start with defaults to ensure all fields are present
            bankName: '',
            bankAccountNumber: '',
            bankRoutingNumber: '',
            zelleEmail: '',
            zellePhone: '',
            paypalEmail: '',
            paypalLink: '',
            acceptedPaymentMethods: ['stripe'],
            // Then override with actual tenant data
            ...tenant.paymentSettings
          });
          
          console.log('Loaded payment settings from tenant:', tenant.paymentSettings);
        } else {
          console.log('No payment settings found in tenant data, using defaults');
        }
      }
      
      // Then try to refresh from server to get the latest data
      try {
        const refreshedTenant = await refreshTenant();
        
        // If we got refreshed data, update payment settings
        if (refreshedTenant && refreshedTenant.paymentSettings) {
          setPaymentSettings({
            // Start with defaults
            bankName: '',
            bankAccountNumber: '',
            bankRoutingNumber: '',
            zelleEmail: '',
            zellePhone: '',
            paypalEmail: '',
            paypalLink: '',
            acceptedPaymentMethods: ['stripe'],
            // Then override with fresh data
            ...refreshedTenant.paymentSettings
          });
          console.log('Updated payment settings from refreshed tenant data');
        }
      } catch (refreshError) {
        console.warn('Could not refresh tenant data from server:', refreshError);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  // Set form default values when tenant data is available or when refreshed
  useEffect(() => {
    loadInitialData();
  }, [tenant?._id]); // Only run when tenant ID changes
  
  // Refresh data when tab changes to ensure we have latest settings
  useEffect(() => {
    if (activeTab) {
      loadInitialData();
    }
  }, [activeTab]);

  // Convert file to base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // Handle logo file selection
  const handleLogoChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setError('Logo file is too large. Maximum size is 2MB.');
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file.');
        return;
      }
      
      setLogoFile(file);
      
      // Create preview
      try {
        const base64 = await convertFileToBase64(file);
        setLogoPreview(base64);
        setValue('logo', base64); // Set to form value
      } catch (err) {
        console.error('Error creating logo preview:', err);
        setError('Error processing image. Please try again.');
      }
    }
  };

  // Trigger file input click
  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccess(false);
      setIsLocalOnly(false);
      
      // Use tenantId state variable instead of retrieving from localStorage
      if (!tenantId) {
        setError('Cannot update settings: No tenant ID available');
        setShowDebug(true);
        return;
      }
      
      // Ensure primaryColor is included and valid
      if (!data.primaryColor) {
        data.primaryColor = previewColor || primaryColor || '#3f51b5';
      }
      
      // Use logo from file upload if available
      if (logoPreview) {
        data.logo = logoPreview;
      }
      
      // Log what we're about to save for debugging
      console.log('Saving settings with primaryColor:', data.primaryColor);
      
      // Update tenant settings with directory settings
      try {
        const payload = {
          name: data.name,
          logo: data.logo,
          primaryColor: data.primaryColor,
          settings: {
            allowSelfRegistration: data.allowSelfRegistration,
            directory: settings.directory
          }
        };
        
        // Update the theme color immediately to ensure it's applied
        setPrimaryColor(data.primaryColor);
        
        // Store color in localStorage directly as backup
        const storedTenant = localStorage.getItem('currentTenant');
        if (storedTenant) {
          try {
            const parsedTenant = JSON.parse(storedTenant);
            if (parsedTenant) {
              parsedTenant.primaryColor = data.primaryColor;
              // Also update logo if available
              if (data.logo) {
                parsedTenant.logo = data.logo;
              }
              localStorage.setItem('currentTenant', JSON.stringify(parsedTenant));
            }
          } catch (e) {
            console.error('Error updating primaryColor in localStorage:', e);
          }
        }
        
        // Make sure to send the request to the server first, prioritizing persistence
        const response = await updateTenantSettings(tenantId, payload);
        
        // Force a refresh of the tenant data from server to ensure we have the latest data
        // This helps with data persistence across sessions
        try {
          await refreshTenant();
        } catch (refreshError) {
          console.error('Error refreshing tenant data:', refreshError);
        }
        
        if (response.localOnly) {
          setIsLocalOnly(true);
          setSuccess(true);
          setError('Changes saved locally only. They will synchronize when connectivity is restored.');
        } else {
          setSuccess(true);
        }
        
        // Ensure color is set one more time after server response
        setPrimaryColor(data.primaryColor);
      } catch (updateError) {
        console.error('Failed to update tenant settings:', updateError);
        setIsLocalOnly(true);
        setError(`Error: ${updateError.message || 'Unknown error'}. Changes saved locally.`);
        setShowDebug(true);
        
        // Despite error, ensure color is still applied
        setPrimaryColor(data.primaryColor);
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error updating tenant settings:', error);
      setError(error.response?.data?.message || error.message || 'Failed to update settings');
      setShowDebug(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Custom field form state
  const [customFields, setCustomFields] = useState([]);
  const [originalCustomFields, setOriginalCustomFields] = useState([]);
  const [hasUnsavedFieldChanges, setHasUnsavedFieldChanges] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');
  const [newFieldRequired, setNewFieldRequired] = useState(false);

  // Load custom fields from tenant settings
  useEffect(() => {
    if (tenant?.settings?.membershipFields) {
      const fields = tenant.settings.membershipFields || [];
      setCustomFields(fields);
      setOriginalCustomFields(JSON.parse(JSON.stringify(fields))); // Deep copy
      setHasUnsavedFieldChanges(false);
    }
  }, [tenant]);

  // Check for unsaved changes whenever customFields changes
  useEffect(() => {
    if (!originalCustomFields || customFields.length !== originalCustomFields.length) {
      setHasUnsavedFieldChanges(true);
      return;
    }
    
    const hasChanges = JSON.stringify(customFields) !== JSON.stringify(originalCustomFields);
    setHasUnsavedFieldChanges(hasChanges);
  }, [customFields, originalCustomFields]);

  // Add new custom field
  const addCustomField = () => {
    if (!newFieldName) return;
    
    const sanitizedName = newFieldName
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
    
    // Check if field with this name already exists
    if (customFields.some(field => field.name === sanitizedName)) {
      setError('A field with this name already exists');
      return;
    }
    
    setCustomFields([
      ...customFields,
      {
        name: sanitizedName,
        label: newFieldLabel || newFieldName,
        type: newFieldType,
        required: newFieldRequired
      }
    ]);
    
    // Clear inputs
    setNewFieldName('');
    setNewFieldLabel('');
    setNewFieldType('text');
    setNewFieldRequired(false);
  };

  // Remove custom field
  const removeCustomField = (index) => {
    const updatedFields = [...customFields];
    updatedFields.splice(index, 1);
    setCustomFields(updatedFields);
  };

  // Save custom fields
  const saveCustomFields = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccess(false);
      setIsLocalOnly(false);
      
      // Use tenantId state variable instead of retrieving from localStorage
      if (!tenantId) {
        setError('Cannot update settings: No tenant ID available');
        setShowDebug(true);
        return;
      }
      
      // Update tenant settings with custom fields
      try {
        const response = await updateTenantSettings(tenantId, {
          settings: {
            membershipFields: customFields
          }
        });
        
        setOriginalCustomFields(JSON.parse(JSON.stringify(customFields)));
        setHasUnsavedFieldChanges(false);
        
        // Force a refresh of the tenant data to ensure we have the most current data
        try {
          await refreshTenant();
        } catch (refreshError) {
          console.error('Error refreshing tenant data:', refreshError);
        }
        
        if (response.localOnly) {
          setIsLocalOnly(true);
          setSuccess(true);
          setError('Custom fields saved locally only. They will synchronize when connectivity is restored.');
        } else {
          setSuccess(true);
        }
      } catch (updateError) {
        console.error('Failed to update custom fields:', updateError);
        setIsLocalOnly(true);
        setError(`Error: ${updateError.message || 'Unknown error'}. Changes saved locally.`);
        setShowDebug(true);
        
        // Still update local state so user knows their changes are saved
        setOriginalCustomFields(JSON.parse(JSON.stringify(customFields)));
        setHasUnsavedFieldChanges(false);
        setSuccess(true);
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error updating custom fields:', error);
      setError(error.response?.data?.message || error.message || 'Failed to update custom fields');
      setShowDebug(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Save directory settings
  const saveDirectorySettings = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccess(false);
      setIsLocalOnly(false);
      
      // Check authentication status first
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn("No authentication token found - settings will be saved locally only");
        setIsLocalOnly(true);
      }
      
      // Use tenantId state variable instead of retrieving from localStorage
      if (!tenantId) {
        setError('Cannot update settings: No tenant ID available');
        setShowDebug(true);
        setIsLoading(false);
        return;
      }
      
      console.log('Saving directory settings with:', {
        enabled: settings.directory?.enabled,
        visibleFields: settings.directory?.visibleFields,
        tenantId,
        localOnly: !token
      });
      
      // Prepare data for API
      const updatedSettings = {
        settings: {
          directory: settings.directory
        }
      };
      
      // Use the tenant context's updateTenantSettings method
      const response = await updateTenantSettings(tenantId, updatedSettings);
      
      // Force a refresh of the tenant data to ensure we have the most current settings
      try {
        await refreshTenant();
      } catch (refreshError) {
        console.error('Error refreshing tenant data:', refreshError);
      }
      
      // Check for local-only response
      if (response.localOnly) {
        setIsLocalOnly(true);
        setSuccess(true);
        console.log("Settings updated locally only due to authentication issues");
        
        // Update local state
        if (tenant) {
          const updatedTenant = JSON.parse(JSON.stringify(tenant));
          if (!updatedTenant.settings) updatedTenant.settings = {};
          updatedTenant.settings.directory = settings.directory;
        }
      } else {
        console.log('Directory settings update response:', response);
        setSuccess(true);
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error in saveDirectorySettings:', error);
      
      // Even if API update fails, still apply changes locally
      setIsLocalOnly(true);
      
      // Show informative error message
      const errorMessage = error.response?.status === 401 
        ? 'Not logged in or session expired. Settings saved locally only.'
        : `Error: ${error.message || 'Unknown error'}. Settings saved locally.`;
        
      setError(errorMessage);
      
      // Update local tenant settings
      if (tenant) {
        const updatedTenant = JSON.parse(JSON.stringify(tenant));
        if (!updatedTenant.settings) updatedTenant.settings = {};
        updatedTenant.settings.directory = settings.directory;
        localStorage.setItem('currentTenant', JSON.stringify(updatedTenant));
      }
      
      // Still show success since we saved locally
      setSuccess(true);
      
      // Show debug info for troubleshooting
      setShowDebug(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Save payment settings
  const savePaymentSettings = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccess(false);
      setIsLocalOnly(false);
      
      // Use tenantId state variable instead of retrieving from localStorage
      if (!tenantId) {
        setError('Cannot update settings: No tenant ID available');
        setShowDebug(true);
        return;
      }
      
      // Create payload with payment settings
      const payload = {
        paymentSettings: paymentSettings
      };
      
      console.log('Saving payment settings:', payload);
      
      // First, update the current tenant object in localStorage to ensure persistence
      try {
        const storedTenant = localStorage.getItem('currentTenant');
        if (storedTenant) {
          const parsedTenant = JSON.parse(storedTenant);
          if (parsedTenant) {
            parsedTenant.paymentSettings = paymentSettings;
            localStorage.setItem('currentTenant', JSON.stringify(parsedTenant));
            console.log('Updated payment settings in local storage');
          }
        }
      } catch (localStorageError) {
        console.error('Error updating payment settings in localStorage:', localStorageError);
      }
      
      // Use the tenant context's updateTenantSettings method to send to server
      try {
        const response = await updateTenantSettings(tenantId, payload);
        
        // Force a refresh of the tenant data to ensure we have the most current settings
        try {
          const refreshedTenant = await refreshTenant();
          console.log('Successfully refreshed tenant data after payment settings update');
          
          // If we got refreshed data with payment settings, update our state
          if (refreshedTenant && refreshedTenant.paymentSettings) {
            setPaymentSettings({
              // Start with defaults
              bankName: '',
              bankAccountNumber: '',
              bankRoutingNumber: '',
              zelleEmail: '',
              zellePhone: '',
              paypalEmail: '',
              paypalLink: '',
              acceptedPaymentMethods: ['stripe'],
              // Then override with fresh data
              ...refreshedTenant.paymentSettings
            });
          }
        } catch (refreshError) {
          console.error('Error refreshing tenant data after payment settings update:', refreshError);
        }
        
        if (response.localOnly) {
          setIsLocalOnly(true);
          setSuccess(true);
          setError('Payment settings saved locally only. They will synchronize when connectivity is restored.');
        } else {
          setSuccess(true);
        }
      } catch (updateError) {
        console.error('Failed to update payment settings:', updateError);
        setIsLocalOnly(true);
        setError(`Error: ${updateError.message || 'Unknown error'}. Changes saved locally.`);
        setShowDebug(true);
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error updating payment settings:', error);
      setError(error.response?.data?.message || error.message || 'Failed to update payment settings');
      setShowDebug(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle payment method checkboxes
  const handlePaymentMethodChange = (method, checked) => {
    const updatedMethods = checked
      ? [...paymentSettings.acceptedPaymentMethods, method]
      : paymentSettings.acceptedPaymentMethods.filter(m => m !== method);
    
    setPaymentSettings({
      ...paymentSettings,
      acceptedPaymentMethods: updatedMethods
    });
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    // Prompt user if there are unsaved changes when switching tabs
    if (hasUnsavedFieldChanges && activeTab === 'fields' && newValue !== 'fields') {
      if (window.confirm('You have unsaved changes to your custom fields. Do you want to save them before leaving this tab?')) {
        saveCustomFields();
      }
    }
    setActiveTab(newValue);
  };

  // Reset custom fields to server state
  const resetCustomFields = () => {
    setCustomFields(JSON.parse(JSON.stringify(originalCustomFields)));
    setHasUnsavedFieldChanges(false);
  };

  // Handle color picker change with better update logic
  const handleColorChange = (e) => {
    const newColor = e.target.value;
    
    // Update the form value
    reset({
      ...watch(),
      primaryColor: newColor
    });
    
    // Show preview of the color immediately
    setPreviewColor(newColor);
    setPrimaryColor(newColor);
    
    console.log('Color changed to:', newColor);
  };
  
  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        mt: { xs: 1, md: 4 }, 
        mb: 4,
        px: { xs: 2, md: 3 }
      }}
    >
      {/* Header Section with dynamic background color based on selected primary color */}
      <Paper 
        elevation={0} 
        sx={{ 
          mb: 4, 
          p: 3, 
          borderRadius: 2,
          background: `linear-gradient(135deg, ${previewColor || '#3f51b5'} 0%, ${previewColor ? adjustColor(previewColor, 20) : '#5c6bc0'} 100%)`,
          color: 'white',
          transition: 'background 0.3s ease'
        }}
      >
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
          <BusinessIcon sx={{ mr: 1 }} /> Organization Settings
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.8 }}>
          Configure your organization's settings and customization
        </Typography>
      </Paper>

      {/* Success Message */}
      {success && (
        <Alert 
          severity={isLocalOnly ? "warning" : "success"}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          <Typography variant="body1">
            {isLocalOnly 
              ? "Changes saved locally only. They will be visible in this session but not saved to the server." 
              : "Changes saved successfully!"}
          </Typography>
          {isLocalOnly && (
            <Button 
              onClick={() => setShowDebug(!showDebug)} 
              size="small"
              sx={{ mt: 1 }}
            >
              {showDebug ? 'Hide' : 'Show'} Debug Info
            </Button>
          )}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert 
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
        >
          <Typography variant="body1">{error}</Typography>
          <Button 
            onClick={() => setShowDebug(!showDebug)} 
            size="small"
            sx={{ mt: 1 }}
          >
            {showDebug ? 'Hide' : 'Show'} Debug Info
          </Button>
        </Alert>
      )}

      {/* Debug Info */}
      {showDebug && (
        <Paper elevation={2} sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CodeIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="subtitle1" fontWeight="medium">Debug Information</Typography>
          </Box>
          <Box 
            component="pre" 
            sx={{ 
              overflow: 'auto', 
              p: 2, 
              bgcolor: 'background.paper', 
              border: '1px solid rgba(0, 0, 0, 0.12)', 
              borderRadius: 1,
              fontSize: '0.75rem'
            }}
          >
            {JSON.stringify({
              tenant: {
                id: tenant?._id || tenant?.id || 'No ID',
                name: tenant?.name || 'No name',
                settings: tenant?.settings || {}
              },
              apiUrl: api?.defaults?.baseURL || 'Not available',
              endpoints: {
                updateTenant: `${api?.defaults?.baseURL || ''}${ENDPOINT}/${tenantId || 'unknown'}?tenantId=${tenantId || 'unknown'}`,
                updateSettings: `${api?.defaults?.baseURL || ''}${ENDPOINT}/${tenantId || 'unknown'}/settings?tenantId=${tenantId || 'unknown'}`
              },
              auth: {
                hasToken: !!localStorage.getItem('token'),
                tokenStart: localStorage.getItem('token')?.substring(0, 10) + '...' || 'No token',
                currentTenantId: tenantId || 'Not set'
              }
            }, null, 2)}
          </Box>
        </Paper>
      )}

      {/* Settings Tabs */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ 
            bgcolor: 'rgba(63, 81, 181, 0.03)',
            borderBottom: '1px solid rgba(0,0,0,0.08)',
          }}
          TabIndicatorProps={{
            style: { backgroundColor: previewColor || '#3f51b5' }
          }}
        >
          <Tab 
            value="general" 
            label="General Settings" 
            icon={<SettingsIcon sx={{ mb: 0.5 }} />} 
            iconPosition="start"
          />
          <Tab 
            value="fields" 
            label={
              hasUnsavedFieldChanges ? (
                <Chip 
                  color="error" 
                  variant="dot"
                  sx={{ '& .MuiBadge-badge': { top: -2, right: -6 } }}
                  label="Custom Fields"
                />
              ) : "Custom Fields"
            }
            icon={<ViewListIcon sx={{ mb: 0.5 }} />} 
            iconPosition="start"
          />
          <Tab 
            value="payment" 
            label="Payment Settings" 
            icon={<PaymentIcon sx={{ mb: 0.5 }} />} 
            iconPosition="start"
          />
          <Tab 
            value="password" 
            label="Change Password" 
            icon={<SettingsIcon sx={{ mb: 0.5 }} />} 
            iconPosition="start"
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* General Settings Tab */}
          {activeTab === 'general' && (
            <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ '& .MuiTextField-root': { mb: 3 } }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Organization Name"
                    variant="outlined"
                    {...register('name', { required: 'Organization name is required' })}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>Organization Logo</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ mr: 2, position: 'relative' }}>
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                          <IconButton 
                            size="small" 
                            sx={{ 
                              bgcolor: 'primary.main', 
                              color: 'white',
                              '&:hover': { bgcolor: 'primary.dark' },
                              width: 32,
                              height: 32
                            }}
                            onClick={handleUploadClick}
                          >
                            <PhotoCameraIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <Avatar 
                          src={logoPreview} 
                          alt="Organization Logo"
                          sx={{ 
                            width: 100, 
                            height: 100,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            bgcolor: logoPreview ? 'white' : 'primary.light'
                          }}
                        >
                          {!logoPreview && <BusinessIcon fontSize="large" />}
                        </Avatar>
                      </Badge>
                    </Box>
                    <Box>
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleLogoChange}
                      />
                      <Button
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        onClick={handleUploadClick}
                        sx={{ mb: 1 }}
                      >
                        Upload Logo
                      </Button>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Recommended size: 200x200 pixels. Maximum file size: 2MB.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>Primary Color</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      component="input" 
                      type="color"
                      value={watchedPrimaryColor || '#3f51b5'}
                      onChange={handleColorChange}
                      sx={{ 
                        width: 80, 
                        height: 40, 
                        p: 0, 
                        border: 'none',
                        borderRadius: 1,
                        mr: 2,
                        cursor: 'pointer'
                      }}
                    />
                    <TextField
                      {...register('primaryColor')}
                      placeholder="#4f46e5"
                      sx={{ width: 200 }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Changes to the primary color will be applied immediately for preview and saved when you click "Save Settings".
                  </Typography>
                  
                  {/* Color Preview Section */}
                  <Box sx={{ mt: 2, p: 2, border: '1px dashed rgba(0,0,0,0.1)', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>Color Preview</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                      <Button 
                        variant="contained" 
                        size="small"
                        sx={{ bgcolor: watchedPrimaryColor || '#3f51b5' }}
                      >
                        Primary Button
                      </Button>
                      <Button 
                        variant="outlined" 
                        size="small"
                        sx={{ 
                          borderColor: watchedPrimaryColor || '#3f51b5',
                          color: watchedPrimaryColor || '#3f51b5'
                        }}
                      >
                        Outlined Button
                      </Button>
                      <Chip 
                        label="Chip Component" 
                        sx={{ 
                          bgcolor: watchedPrimaryColor || '#3f51b5',
                          color: 'white'
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <FormGroup>
                    <FormControlLabel 
                      control={
                        <Switch 
                          color="primary" 
                          {...register('allowSelfRegistration')} 
                        />
                      } 
                      label={
                        <Box>
                          <Typography variant="body1">Allow Self Registration</Typography>
                          <Typography variant="body2" color="text.secondary">
                            When enabled, users can register for your organization without an invitation.
                          </Typography>
                        </Box>
                      }
                    />
                  </FormGroup>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isLoading}
                      sx={{
                        background: `linear-gradient(45deg, ${watchedPrimaryColor || '#3f51b5'} 30%, ${adjustColor(watchedPrimaryColor || '#3f51b5', 20)} 90%)`,
                        boxShadow: '0 4px 6px rgba(63, 81, 181, 0.25)',
                        px: 4,
                        transition: 'background 0.3s ease'
                      }}
                    >
                      {isLoading ? (
                        <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                      ) : 'Save Settings'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Directory Settings Panel */}
          {activeTab === 'general' && (
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3, 
                borderRadius: 2, 
                mb: 4,
                borderLeft: '4px solid #4CAF50',
                mt: 4 
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ color: '#4CAF50', display: 'flex', alignItems: 'center' }}>
                <PeopleIcon sx={{ mr: 1 }} /> Member Directory Settings
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.directory?.enabled || false}
                        onChange={(e) => {
                          const updatedSettings = {
                            ...settings,
                            directory: {
                              ...(settings.directory || {}),
                              enabled: e.target.checked
                            }
                          };
                          setSettings(updatedSettings);
                        }}
                      />
                    }
                    label="Enable Member Directory"
                  />
                  <Typography variant="caption" display="block" color="text.secondary">
                    When enabled, members can see other members in a directory view
                  </Typography>
                </Grid>
                
                {settings.directory?.enabled && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Visible Member Fields
                      </Typography>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Select which fields should be visible in the member directory
                        </Typography>
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" gutterBottom color="primary">
                              Standard Fields
                            </Typography>
                            <FormGroup>
                              {['firstName', 'lastName', 'email', 'membershipType', 'joinDate'].map((field) => (
                                <FormControlLabel
                                  key={field}
                                  control={
                                    <Checkbox
                                      checked={settings.directory?.visibleFields?.includes(field) || false}
                                      onChange={(e) => {
                                        const currentFields = settings.directory?.visibleFields || [];
                                        const updatedFields = e.target.checked
                                          ? [...currentFields, field]
                                          : currentFields.filter(f => f !== field);
                                        
                                        const updatedSettings = {
                                          ...settings,
                                          directory: {
                                            ...(settings.directory || {}),
                                            visibleFields: updatedFields
                                          }
                                        };
                                        setSettings(updatedSettings);
                                      }}
                                    />
                                  }
                                  label={field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')}
                                />
                              ))}
                            </FormGroup>
                          </Paper>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" gutterBottom color="primary">
                              Custom Fields
                            </Typography>
                            
                            {customFields && customFields.length > 0 ? (
                              <FormGroup>
                                {customFields.map((field) => (
                                  <FormControlLabel
                                    key={field.name}
                                    control={
                                      <Checkbox
                                        checked={settings.directory?.visibleFields?.includes(field.name) || false}
                                        onChange={(e) => {
                                          const currentFields = settings.directory?.visibleFields || [];
                                          const updatedFields = e.target.checked
                                            ? [...currentFields, field.name]
                                            : currentFields.filter(f => f !== field.name);
                                          
                                          const updatedSettings = {
                                            ...settings,
                                            directory: {
                                              ...(settings.directory || {}),
                                              visibleFields: updatedFields
                                            }
                                          };
                                          setSettings(updatedSettings);
                                        }}
                                      />
                                    }
                                    label={
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {field.label || field.name}
                                        <Chip 
                                          size="small" 
                                          label={field.type} 
                                          sx={{ ml: 1, fontSize: '0.7rem' }}
                                        />
                                      </Box>
                                    }
                                  />
                                ))}
                              </FormGroup>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                No custom fields defined yet. Add them in the Custom Fields tab.
                              </Typography>
                            )}
                          </Paper>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={saveDirectorySettings}
                        disabled={isLoading}
                        startIcon={isLoading ? <CircularProgress size={20} /> : null}
                      >
                        {isLoading ? 'Saving...' : 'Save Directory Settings'}
                      </Button>
                    </Grid>
                  </>
                )}
              </Grid>
            </Paper>
          )}

          {/* Custom Fields Tab */}
          {activeTab === 'fields' && (
            <Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Define custom fields to collect additional information from your members.
              </Typography>

              {/* List of existing custom fields */}
              <Paper 
                elevation={1} 
                sx={{ 
                  mb: 4, 
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: 'rgba(0,0,0,0.01)'
                }}
              >
                <Box 
                  sx={{ 
                    p: 2, 
                    pl: 3, 
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Typography variant="h6" fontWeight="medium">Custom Fields</Typography>
                </Box>
                
                {customFields.length > 0 ? (
                  <List sx={{ p: 0 }}>
                    {customFields.map((field, index) => (
                      <ListItem 
                        key={index} 
                        sx={{ 
                          borderBottom: index < customFields.length - 1 ? '1px solid rgba(0,0,0,0.06)' : 'none',
                          py: 2,
                          px: 3,
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' }
                        }}
                        secondaryAction={
                          <IconButton 
                            edge="end" 
                            aria-label="delete"
                            onClick={() => removeCustomField(index)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        }
                      >
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body1" fontWeight="medium">
                              {field.label || field.name}
                            </Typography>
                            <Chip 
                              label={field.type}
                              size="small"
                              sx={{ ml: 1, bgcolor: 'rgba(0,0,0,0.05)' }}
                            />
                            {field.required && (
                              <Chip 
                                label="Required"
                                size="small"
                                color="error"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Field name: {field.name}
                          </Typography>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">No custom fields defined yet.</Typography>
                  </Box>
                )}

                {customFields.length > 0 && (
                  <Box sx={{ p: 2, textAlign: 'right', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                    <Button
                      onClick={saveCustomFields}
                      disabled={isLoading}
                      variant="contained"
                      color="primary"
                      sx={{
                        background: 'linear-gradient(45deg, #3f51b5 30%, #5c6bc0 90%)',
                      }}
                    >
                      {isLoading ? (
                        <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                      ) : 'Save Custom Fields'}
                    </Button>
                  </Box>
                )}
              </Paper>

              {/* Add new custom field */}
              <Paper elevation={2} sx={{ borderRadius: 2, p: 3 }}>
                <Typography variant="h6" fontWeight="medium" sx={{ mb: 2 }}>
                  Add New Field
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Field Name"
                      value={newFieldName}
                      onChange={(e) => setNewFieldName(e.target.value)}
                      placeholder="e.g., phoneNumber"
                      helperText="Internal field name (no spaces or special characters)"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Display Label"
                      value={newFieldLabel}
                      onChange={(e) => setNewFieldLabel(e.target.value)}
                      placeholder="e.g., Phone Number"
                      helperText="Human-readable field label (leave blank to use field name)"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Field Type"
                      value={newFieldType}
                      onChange={(e) => setNewFieldType(e.target.value)}
                      SelectProps={{
                        native: true,
                      }}
                    >
                      <option value="text">Text</option>
                      <option value="number">Number</option>
                      <option value="date">Date</option>
                      <option value="select">Dropdown</option>
                      <option value="checkbox">Checkbox</option>
                    </TextField>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControlLabel 
                      control={
                        <Switch 
                          checked={newFieldRequired}
                          onChange={(e) => setNewFieldRequired(e.target.checked)}
                          color="primary" 
                        />
                      } 
                      label="Required Field"
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ textAlign: 'right' }}>
                      <Button
                        onClick={addCustomField}
                        disabled={!newFieldName}
                        variant="contained"
                        startIcon={<AddIcon />}
                        sx={{
                          background: 'linear-gradient(45deg, #009688 30%, #4DB6AC 90%)',
                        }}
                      >
                        Add Field
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}

          {/* Payment Settings Tab */}
          {activeTab === 'payment' && (
            <Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Configure payment options for your members. The default Stripe payment gateway will be used for subscriptions, but you can also provide alternative payment methods.
              </Typography>

              <Paper elevation={2} sx={{ mb: 4, p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Accepted Payment Methods
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Select which payment methods your members can use for payments.
                </Typography>
                
                <FormGroup sx={{ mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={paymentSettings.acceptedPaymentMethods.includes('stripe')}
                        onChange={(e) => handlePaymentMethodChange('stripe', e.target.checked)}
                        disabled={true} // Stripe is always enabled as the default gateway
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body1">Stripe (Default)</Typography>
                        <Chip 
                          size="small" 
                          label="Required" 
                          sx={{ ml: 1, bgcolor: 'primary.main', color: 'white' }} 
                        />
                      </Box>
                    }
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mb: 1, display: 'block' }}>
                    Stripe is the default payment gateway for all subscription payments.
                  </Typography>
                  
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={paymentSettings.acceptedPaymentMethods.includes('bank')}
                        onChange={(e) => handlePaymentMethodChange('bank', e.target.checked)}
                      />
                    }
                    label="Bank Transfer"
                  />
                  
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={paymentSettings.acceptedPaymentMethods.includes('zelle')}
                        onChange={(e) => handlePaymentMethodChange('zelle', e.target.checked)}
                      />
                    }
                    label="Zelle"
                  />
                  
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={paymentSettings.acceptedPaymentMethods.includes('paypal')}
                        onChange={(e) => handlePaymentMethodChange('paypal', e.target.checked)}
                      />
                    }
                    label="PayPal"
                  />
                </FormGroup>
              </Paper>

              {/* Bank Transfer Details */}
              {paymentSettings.acceptedPaymentMethods.includes('bank') && (
                <Accordion 
                  defaultExpanded 
                  sx={{ mb: 3, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BankIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Bank Transfer Details</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Bank Name"
                          value={paymentSettings.bankName}
                          onChange={(e) => setPaymentSettings({...paymentSettings, bankName: e.target.value})}
                          placeholder="e.g., Chase Bank"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Account Number"
                          value={paymentSettings.bankAccountNumber}
                          onChange={(e) => setPaymentSettings({...paymentSettings, bankAccountNumber: e.target.value})}
                          placeholder="e.g., 123456789"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Routing Number"
                          value={paymentSettings.bankRoutingNumber}
                          onChange={(e) => setPaymentSettings({...paymentSettings, bankRoutingNumber: e.target.value})}
                          placeholder="e.g., 021000021"
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Zelle Details */}
              {paymentSettings.acceptedPaymentMethods.includes('zelle') && (
                <Accordion 
                  defaultExpanded 
                  sx={{ mb: 3, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PaymentIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Zelle Details</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Zelle Email"
                          value={paymentSettings.zelleEmail}
                          onChange={(e) => setPaymentSettings({...paymentSettings, zelleEmail: e.target.value})}
                          placeholder="e.g., payments@example.com"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Zelle Phone Number"
                          value={paymentSettings.zellePhone}
                          onChange={(e) => setPaymentSettings({...paymentSettings, zellePhone: e.target.value})}
                          placeholder="e.g., (555) 123-4567"
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* PayPal Details */}
              {paymentSettings.acceptedPaymentMethods.includes('paypal') && (
                <Accordion 
                  defaultExpanded 
                  sx={{ mb: 3, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PaymentIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">PayPal Details</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="PayPal Email"
                          value={paymentSettings.paypalEmail}
                          onChange={(e) => setPaymentSettings({...paymentSettings, paypalEmail: e.target.value})}
                          placeholder="e.g., payments@example.com"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="PayPal Payment Link (Optional)"
                          value={paymentSettings.paypalLink}
                          onChange={(e) => setPaymentSettings({...paymentSettings, paypalLink: e.target.value})}
                          placeholder="e.g., https://paypal.me/yourorganization"
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              )}

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={savePaymentSettings}
                  disabled={isLoading}
                  startIcon={isLoading ? <CircularProgress size={20} /> : null}
                  sx={{
                    background: `linear-gradient(45deg, ${previewColor || '#3f51b5'} 30%, ${adjustColor(previewColor || '#3f51b5', 20)} 90%)`,
                    px: 4
                  }}
                >
                  {isLoading ? 'Saving...' : 'Save Payment Settings'}
                </Button>
              </Box>
            </Box>
          )}

          {/* Password Change Tab */}
          {activeTab === 'password' && (
            <Box component="form" onSubmit={handleSubmitPassword(onPasswordSubmit)}>
              <Typography variant="h6" gutterBottom>
                Change Password
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Update your account password. Make sure to use a strong and unique password.
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Current Password"
                    type="password"
                    {...registerPassword('currentPassword', { required: 'Current password is required' })}
                    error={!!passwordErrors.currentPassword}
                    helperText={passwordErrors.currentPassword?.message}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="New Password"
                    type="password"
                    {...registerPassword('newPassword', { required: 'New password is required' })}
                    error={!!passwordErrors.newPassword}
                    helperText={passwordErrors.newPassword?.message}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type="password"
                    {...registerPassword('confirmPassword', { required: 'Please confirm your new password' })}
                    error={!!passwordErrors.confirmPassword}
                    helperText={passwordErrors.confirmPassword?.message}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isLoading}
                      sx={{
                        background: `linear-gradient(45deg, ${previewColor || '#3f51b5'} 30%, ${adjustColor(previewColor || '#3f51b5', 20)} 90%)`,
                        px: 4
                      }}
                    >
                      {isLoading ? (
                        <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                      ) : 'Change Password'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

// Helper function to adjust color for gradients
function adjustColor(color, amount) {
  if (!color) return '#5c6bc0'; // Default lighter blue if no color provided
  
  // Function to convert hex to RGB
  const hexToRgb = (hex) => {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const fullHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
    
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  // Function to convert RGB to hex
  const rgbToHex = (r, g, b) => {
    const componentToHex = (c) => {
      const hex = Math.max(0, Math.min(255, c)).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
  };

  const rgb = hexToRgb(color);
  if (!rgb) return color;
  
  return rgbToHex(
    Math.min(255, rgb.r + amount),
    Math.min(255, rgb.g + amount),
    Math.min(255, rgb.b + amount)
  );
}

export default TenantSettingsPage;
